import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import SingleMenu from "@/app/AppShellSingleMenu.jsx";
import ExpandRight from "@/inline-assets/expand-right.svg";
import { classNames } from "@/util/class-names.mjs";

const MenuListItemWrapper = styled("li")`
  position: relative;

  &.highlight {
    .link {
      color: var(--game-accent-color) !important;
      background: color-mix(
        in hsl,
        var(--game-accent-color) 10%,
        transparent
      ) !important;
    }
  }

  & > .link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-4);
    width: 100%;
    min-width: calc(var(--sp-1) * 38);
    padding: var(--sp-1) var(--sp-2);
    text-align: left;
    background-color: transparent;
    border-radius: var(--br);
    color: var(--shade1);
    cursor: pointer;
    white-space: nowrap;
    transition:
      background var(--transition),
      box-shadow var(--transition);
  }

  &.has-menu {
    & > .link {
      padding: var(--sp-1);
      padding-left: var(--sp-4);
    }
  }

  .single-menu {
    display: none;
    position: absolute;
    top: calc(-1 * var(--br-lg));
    left: calc(100% - var(--br-lg));
    border-radius: var(--br-lg);
    border: 1px solid var(--shade3-15);
    background: var(--shade7);
    box-shadow:
      0px 12px 24px 0px rgba(7, 8, 12, 0.25),
      0px 4px 12px 0px rgba(7, 8, 12, 0.25);
    z-index: 1;
  }

  &:hover {
    & > .link {
      opacity: 1 !important;
      color: var(--shade0);
      background: var(--shade6);
    }

    .single-menu {
      display: flex;
    }
  }

  &.active {
    > span {
      color: var(--shade0);
    }
  }

  svg.expand-right {
    width: var(--sp-4_5);
    height: var(--sp-4_5);
  }
`;

export type MenuListItemProps = {
  text: Translation | string;
  href?: string;
  target?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  highlight?: boolean;

  Icon?: React.ReactNode;
  menu?: {
    category?: Translation;

    items?: MenuListItemProps[];
  };

  className?: string;
  setFade?: (value: boolean) => void;
} & (
  | { href: string }
  | {
      onClick: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    }
);

export default function MenuListItem({
  text,
  href,
  onClick,
  target,
  Icon,
  menu,
  className,
  highlight,
}: MenuListItemProps) {
  const { t } = useTranslation();

  return (
    <MenuListItemWrapper
      {...classNames(className, menu && "has-menu", highlight && "highlight")}
    >
      {href ? (
        <a className="link" href={href} target={target} onClick={onClick}>
          <span>{Array.isArray(text) ? t(...text) : text}</span>
          {Icon}
          {menu && <ExpandRight className="expand-right" />}
        </a>
      ) : (
        <button className="link" onClick={onClick}>
          <span>{Array.isArray(text) ? t(...text) : text}</span>
          {Icon}
          {menu && <ExpandRight className="expand-right" />}
        </button>
      )}
      {menu && <SingleMenu className="single-menu" menu={menu} />}
    </MenuListItemWrapper>
  );
}
